import { PreviewOptions } from '../../../extensions';
import { groupBy, uniqBy } from 'lodash';
import { QuestionnaireCategory } from '../../QuestionnaireResults/schema';

function getDebugScore(
  negativeCategory: QuestionnaireCategory | undefined,
  totals: Record<string, number | undefined>,
  count: Record<string, number | undefined>,
) {
  if (!negativeCategory) {
    return { debugTotals: totals, debugCount: count };
  }

  const sourceTotal = totals[negativeCategory.sourceCategory ?? '__'] ?? 0;
  const sourceCount = count[negativeCategory.sourceCategory ?? '__'] ?? 0;

  const negativeCount = sourceTotal - sourceCount;

  const debugTotals = {
    ...totals,
    [negativeCategory.id]: sourceTotal,
  };
  const debugCount = {
    ...count,
    [negativeCategory.id]: negativeCount,
  };

  return { debugTotals, debugCount };
}

function increment(category: string, acc: Record<string, number>) {
  return {
    ...acc,
    [category]: (acc[category] ?? 0) + 1,
  };
}

export function getPercentScore(
  previewOptions: PreviewOptions,
  formData: Record<string, any>,
  options?: { mapToDefault?: boolean; debugEnabled?: boolean },
) {
  const { mappings, categories } = previewOptions;
  if (!mappings || !categories) {
    return {};
  }
  const negativeCategory = categories.find(c => c.isNegative);
  const defaultCategory = categories.find(c => c.isDefault);
  const { mapToDefault = false, debugEnabled = false } = options ?? {};

  const defaultCategoryTotals = {
    [defaultCategory?.id ?? '__']: Object.keys(mappings).length,
  };

  const categoryTotals = Object.fromEntries(
    Object.entries(
      groupBy(
        Object.keys(formData).flatMap(key =>
          uniqBy(mappings[key], it => it.category),
        ),
        it => it.category,
      ),
    ).map(([key, elements]) => [key, elements.length]),
  );
  const totals = {
    ...defaultCategoryTotals,
    ...categoryTotals,
  };
  const categoryScore: Record<string, number> = Object.entries(formData).reduce(
    (acc, item) => {
      const [key, value] = item;
      const metric = mappings[key];
      if (!metric) {
        return acc;
      }

      const selectedValue = metric.find(it => it.value === value);

      if (!selectedValue?.category) {
        return mapToDefault && defaultCategory
          ? increment(defaultCategory.id, acc)
          : acc;
      }

      return increment(selectedValue.category, acc);
    },
    {} as Record<string, number>,
  );

  if (debugEnabled) {
    const { debugCount, debugTotals } = getDebugScore(
      negativeCategory,
      totals,
      categoryScore,
    );
    // eslint-disable-next-line no-console
    console.log('[Percentage]:', 'Total:', debugTotals, 'Counted', debugCount);
  }

  const scoreByCategory = Object.fromEntries(
    categories
      .filter(it => !it.isNegative)
      .map(c => {
        const total = totals[c.id] ?? -1;
        const scored = categoryScore[c.id] ?? 0;
        return [c.id, (scored / total) * 100];
      }),
  );
  const negativeScore = negativeCategory?.sourceCategory
    ? {
        [negativeCategory.id]:
          100 - scoreByCategory[negativeCategory.sourceCategory],
      }
    : {};
  return {
    ...scoreByCategory,
    ...negativeScore,
  };
}
